import { Link } from '@tanstack/react-router';
import { CaretRight } from '@phosphor-icons/react';
import type { ProjectResponse } from '@mybusinessmate/schema';
import { projectDateLabel, projectStatusDisplay } from '../../lib/project-display';
import { weekdayDay } from '../../lib/task-display';
import { Icon } from '../Icon';
import { Chip } from '../ui/chip';
import { ProgressBar } from '../ui/progress-bar';
import { Skeleton } from '../ui/skeleton';
import { StatusTag } from '../ui/status-tag';
import { DomainTile } from './domain-tile';

const DOMAIN_TONE = { Website: 'website', Marketing: 'marketing', Automation: 'automation' } as const;

/**
 * A project on the client's Projects page (Figma C03.1): its domain tile and name, what needs the client ("1 to
 * review") or where it stands, the date, the next task, and the done/total count under a bar in the domain's colour.
 * The bar is the stored rollup of its tasks.
 */
export function ProjectCard({ project }: { project: ProjectResponse }) {
  const review = project.waitingForClientCount;
  const next = project.nextTask;
  return (
    <Link
      to="/subdomain/app/projects/$projectId"
      params={{ projectId: project.id }}
      className="flex flex-col gap-(--mbm-space-3) rounded-(--mbm-component-radius-widget) bg-surface-card p-(--mbm-space-5) shadow-(--mbm-shadow-table-card) transition-opacity duration-(--mbm-duration-fast) hover:opacity-(--mbm-opacity-pressed) focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
    >
      <span className="flex items-center gap-(--mbm-space-3)">
        <DomainTile domain={project.domain} />
        <span className="min-w-0 flex-1 text-label-large text-text-primary">{project.name}</span>
        <span className="text-text-muted">
          <Icon icon={CaretRight} size="md" />
        </span>
      </span>

      <span className="flex flex-wrap gap-(--mbm-space-1-5)">
        {review > 0 ? (
          <StatusTag tone="blue" label={`${review} to review`} />
        ) : (
          <Chip>{projectStatusDisplay(project.status).label}</Chip>
        )}
        <Chip>{projectDateLabel(project)}</Chip>
      </span>

      <span className="truncate text-body-small text-text-secondary">
        {next
          ? `Next: ${next.title}${next.deadline ? ` · ${weekdayDay(next.deadline)}` : ''}`
          : 'Nothing open right now'}
      </span>

      <span className="flex items-center gap-(--mbm-space-3)">
        <ProgressBar value={project.progress} tone={DOMAIN_TONE[project.domain]} label={`${project.name} progress`} />
        <span className="shrink-0 text-label-medium text-text-primary">
          {project.doneTaskCount}/{project.taskCount}
        </span>
      </span>
    </Link>
  );
}

export function ProjectCardSkeleton() {
  return (
    <div className="flex flex-col gap-(--mbm-space-4) rounded-(--mbm-component-radius-widget) bg-surface-card p-(--mbm-space-5) shadow-(--mbm-shadow-table-card)">
      <div className="flex items-center gap-(--mbm-space-3)">
        <Skeleton className="size-(--mbm-size-icon-tile-project)" />
        <Skeleton className="h-(--mbm-space-5) w-1/2" />
      </div>
      <Skeleton className="h-(--mbm-space-5) w-2/3" />
      <Skeleton className="h-(--mbm-space-5) w-full" />
    </div>
  );
}
